// Admin cost reporting for Supabase, Netlify, domain and email.
import { SUPABASE_PROJECT_NAME } from "../config.js";
import { escapeHTML } from "../lib/dom.js";
import { moneyCents, moneyEUR, platformCostCategoryLabel } from "../lib/format.js";
import { state } from "../state.js";
import { supabase } from "../supabase-client.js";
import { adminWorkspaceHero } from "./admin.js";

function supabaseUsagePanel() {
  const usage = state.adminSupabaseUsage?.supabase || state.adminSupabaseUsage;
  if (!usage) return "";
  if (!usage.configured) {
    return `<section class="card admin-cost-card">
      <h2>Supabase</h2>
      <p style="color:var(--muted)">${state.language === "it"
        ? "Sync automatico non configurato. Serve un Personal Access Token Supabase salvato come secret Edge Function."
        : "Automatic sync is not configured. A Supabase Personal Access Token must be saved as an Edge Function secret."}</p>
      <code>PLATFORM_SUPABASE_MANAGEMENT_TOKEN</code>
      <p style="color:var(--muted);font-size:13px">${escapeHTML(usage.message || "")}</p>
    </section>`;
  }
  const endpointStatus = [
    [state.language === "it" ? "Piano e add-on" : "Plan and add-ons", usage.addons],
    [state.language === "it" ? "Traffico API" : "API traffic", usage.api_counts],
    [state.language === "it" ? "Richieste API" : "API requests", usage.request_counts]
  ].map(([label, result]) => `
    <div class="compact-row">
      <strong>${label}</strong>
      <span>${result?.ok ? "OK" : `Errore ${result?.status || ""}`}</span>
    </div>`).join("");
  return `<section class="card admin-cost-card">
    <div style="display:flex;justify-content:space-between;gap:12px;align-items:start">
      <div>
        <h2>Supabase</h2>
        <p style="color:var(--muted)">${state.language === "it"
          ? "Dati letti dalla Management API ufficiale: add-on/compute e usage. Le fatture complete non sono esposte da questi endpoint."
          : "Read from the official Management API: add-ons/compute and usage. Full invoices are not exposed by these endpoints."}</p>
      </div>
      <button class="secondary" id="refresh-supabase-usage">${state.language === "it" ? "Aggiorna" : "Refresh"}</button>
    </div>
    <div class="compact-stack">
      <div class="compact-row"><strong>Project</strong><span>${escapeHTML(SUPABASE_PROJECT_NAME)}</span></div>
      <div class="compact-row"><strong>${state.language === "it" ? "Letto il" : "Fetched at"}</strong><span>${usage.fetched_at ? new Date(usage.fetched_at).toLocaleString(state.language) : "—"}</span></div>
      ${endpointStatus}
    </div>
    <details class="technical-details"><summary>${state.language === "it" ? "Dettagli tecnici" : "Technical details"}</summary><code>${escapeHTML(usage.project_ref || "—")}</code></details>
  </section>`;
}

function netlifyUsagePanel() {
  const usage = state.adminSupabaseUsage?.netlify;
  if (!usage) return "";
  if (!usage.configured) {
    return `<section class="card admin-cost-card">
      <h2>Netlify</h2>
      <p style="color:var(--muted)">${state.language === "it"
        ? "Sync automatico non configurato. Serve un token Netlify salvato come secret Edge Function."
        : "Automatic sync is not configured. A Netlify token must be saved as an Edge Function secret."}</p>
      <code>NETLIFY_AUTH_TOKEN</code>
      <p style="color:var(--muted);font-size:13px">${escapeHTML(usage.message || "")}</p>
    </section>`;
  }
  const sitePayload = usage.site?.payload || {};
  const buildPayload = Array.isArray(usage.builds?.payload) ? usage.builds.payload[0] : null;
  const minutes = buildPayload?.minutes || {};
  const deploys = Array.isArray(usage.deploys?.payload) ? usage.deploys.payload : [];
  const latestDeploy = deploys[0];
  return `<section class="card admin-cost-card">
    <div style="display:flex;justify-content:space-between;gap:12px;align-items:start">
      <div>
        <h2>Netlify</h2>
        <p style="color:var(--muted)">${state.language === "it"
          ? "Uso operativo letto dalle API Netlify: sito, deploy e minuti build."
          : "Operational usage read from Netlify APIs: site, deploys and build minutes."}</p>
      </div>
      <button class="secondary" id="refresh-platform-usage">${state.language === "it" ? "Aggiorna" : "Refresh"}</button>
    </div>
    <div class="compact-stack">
      <div class="compact-row"><strong>Site</strong><span>${escapeHTML(sitePayload.name || "—")}</span></div>
      <div class="compact-row"><strong>URL</strong><span>${escapeHTML(sitePayload.ssl_url || sitePayload.url || "—")}</span></div>
      <div class="compact-row"><strong>${state.language === "it" ? "Minuti build mese" : "Build minutes month"}</strong><span>${minutes.current ?? "—"} / ${minutes.included_minutes_with_packs || minutes.included_minutes || "—"}</span></div>
      <div class="compact-row"><strong>${state.language === "it" ? "Deploy ultimo" : "Latest deploy"}</strong><span>${latestDeploy ? `${escapeHTML(latestDeploy.state || "—")} · ${new Date(latestDeploy.created_at).toLocaleString(state.language)}` : "—"}</span></div>
      <div class="compact-row"><strong>${state.language === "it" ? "Letto il" : "Fetched at"}</strong><span>${usage.fetched_at ? new Date(usage.fetched_at).toLocaleString(state.language) : "—"}</span></div>
    </div>
  </section>`;
}

function domainUsagePanel() {
  const usage = state.adminSupabaseUsage?.domain;
  if (!usage) return "";
  const rdap = usage.public?.payload || {};
  const expirationEvent = (rdap.events || []).find(event => event.eventAction === "expiration");
  const expiryDate = expirationEvent?.eventDate ? new Date(expirationEvent.eventDate) : null;
  const daysLeft = expiryDate ? Math.ceil((expiryDate.getTime() - Date.now()) / 86400000) : null;
  const namecheap = usage.namecheap || {};
  const apiInfo = namecheap.info || {};
  const pricing = namecheap.pricing || {};
  const warning = daysLeft !== null && daysLeft <= 30
    ? (state.language === "it" ? "Rinnovo vicino" : "Renewal soon")
    : daysLeft !== null && daysLeft <= 60
      ? (state.language === "it" ? "Da monitorare" : "Monitor")
      : "OK";
  return `<section class="card admin-cost-card">
    <h2>${state.language === "it" ? "Dominio" : "Domain"}</h2>
    <p style="color:var(--muted)">${state.language === "it"
      ? "Monitor pubblico RDAP attivo. Namecheap API resta disattivata finché l’account non supera i requisiti minimi e l’IP non è autorizzato."
      : "Public RDAP monitor is active. Namecheap API stays disabled until the account meets minimum requirements and the IP is authorized."}</p>
    <div class="compact-stack">
      <div class="compact-row"><strong>Domain</strong><span>${escapeHTML(usage.domain || "—")}</span></div>
      <div class="compact-row"><strong>Registrar</strong><span>${escapeHTML(rdap.registrar || rdap.entities?.[0]?.vcardArray?.[1]?.find(row => row[0] === "fn")?.[3] || "—")}</span></div>
      <div class="compact-row"><strong>${state.language === "it" ? "Scadenza" : "Expiration"}</strong><span>${expiryDate ? expiryDate.toLocaleDateString(state.language) : apiInfo.expired_date || "—"}</span></div>
      <div class="compact-row"><strong>${state.language === "it" ? "Giorni mancanti" : "Days left"}</strong><span>${daysLeft ?? "—"} · ${warning}</span></div>
      <div class="compact-row"><strong>Namecheap API</strong><span>${namecheap.configured ? (apiInfo.ok ? "OK" : `Errore: ${(apiInfo.errors || []).join(", ") || apiInfo.api_status || "—"}`) : (state.language === "it" ? "Non disponibile ora" : "Unavailable now")}</span></div>
      <div class="compact-row"><strong>${state.language === "it" ? "Rinnovo stimato" : "Renewal estimate"}</strong><span>${pricing.renew_price ? `${pricing.renew_price} ${pricing.currency || "USD"}` : (state.language === "it" ? "Inserimento manuale" : "Manual entry")}</span></div>
    </div>
    <button class="secondary full" type="button" id="prefill-domain-cost" style="margin-top:12px">${state.language === "it" ? "Registra costo rinnovo dominio" : "Record domain renewal cost"}</button>
  </section>`;
}

function emailCostPanel() {
  const emailCosts = state.adminPlatformCosts.filter(cost => cost.category === "email");
  const monthlyEmailCost = emailCosts
    .filter(cost => {
      const costDate = new Date(`${cost.cost_date}T00:00:00`);
      const now = new Date();
      return costDate.getFullYear() === now.getFullYear() && costDate.getMonth() === now.getMonth();
    })
    .reduce((sum, cost) => sum + Number(cost.amount_cents || 0), 0);
  const latestEmailCost = emailCosts[0];
  return `<section class="card admin-cost-card">
    <h2>Email</h2>
    <p style="color:var(--muted)">${state.language === "it"
      ? "Servizio usato per conferma account, reset password e comunicazioni automatiche."
      : "Service used for account confirmation, password reset and automatic communications."}</p>
    <div class="compact-stack">
      <div class="compact-row"><strong>${state.language === "it" ? "Provider" : "Provider"}</strong><span>${escapeHTML(latestEmailCost?.provider || "Resend / SMTP")}</span></div>
      <div class="compact-row"><strong>${state.language === "it" ? "Costo mese registrato" : "Recorded month cost"}</strong><span>${moneyCents(monthlyEmailCost)}</span></div>
      <div class="compact-row"><strong>${state.language === "it" ? "Ultimo costo" : "Latest cost"}</strong><span>${latestEmailCost ? `${moneyCents(latestEmailCost.amount_cents)} · ${escapeHTML(latestEmailCost.cost_date)}` : "—"}</span></div>
    </div>
    <button class="secondary full" type="button" id="prefill-email-cost" style="margin-top:12px">${state.language === "it" ? "Registra costo email" : "Record email cost"}</button>
  </section>`;
}

export function adminCostsView() {
  const summary = state.adminSummary || {};
  const costRows = state.adminPlatformCosts.length ? state.adminPlatformCosts.map(cost => `
    <tr>
      <td>${escapeHTML(cost.cost_date)}</td>
      <td>${escapeHTML(platformCostCategoryLabel(cost.category))}</td>
      <td>${escapeHTML(cost.provider)}</td>
      <td>${escapeHTML(cost.description || "—")}</td>
      <td>${moneyCents(cost.amount_cents)}</td>
      <td>${escapeHTML(cost.created_by_username || "—")}</td>
    </tr>`).join("") : `<tr><td colspan="6">—</td></tr>`;
  const reviewRows = state.adminTokenReviews.length ? state.adminTokenReviews.map(item => `
    <tr>
      <td>${new Date(item.created_at).toLocaleString(state.language)}</td>
      <td>${escapeHTML(item.username)}</td>
      <td>${escapeHTML(item.exercise_title || "—")}</td>
      <td>${Number(item.total_tokens).toLocaleString()}</td>
      <td>$${Number(item.cost_usd).toFixed(4)}</td>
      <td>${escapeHTML(item.review_reason || "—")}</td>
    </tr>`).join("") : `<tr><td colspan="6">${state.language === "it" ? "Nessuna richiesta sopra soglia." : "No requests over threshold."}</td></tr>`;
  const monthlyCost = Number(state.adminDashboard?.platform_cost_eur_month || 0);
  return `<div class="stack admin-costs-page">
    ${adminWorkspaceHero({
      icon: "€",
      eyebrow: state.language === "it" ? "Controllo economico" : "Cost control",
      title: state.language === "it" ? "Servizi, costi e soglie" : "Services, costs and thresholds",
      description: state.language === "it" ? "Monitora i servizi collegati, registra le spese e individua le richieste AI che richiedono una revisione." : "Monitor connected services, record expenses and identify AI requests that require review.",
      metrics: [
        { label: state.language === "it" ? "costi mese" : "month costs", value: moneyEUR(monthlyCost), tone: "warn" },
        { label: state.language === "it" ? "servizi" : "services", value: "4" },
        { label: state.language === "it" ? "da revisionare" : "to review", value: state.adminTokenReviews.length.toLocaleString(), tone: state.adminTokenReviews.length ? "warn" : "accent" }
      ]
    })}
    <div class="admin-cost-grid admin-service-grid">
    ${supabaseUsagePanel()}
    ${netlifyUsagePanel()}
    ${emailCostPanel()}
    ${domainUsagePanel()}
    <section class="card admin-cost-card">
      <h2>${state.language === "it" ? "Costi sito / piattaforma" : "Site / platform costs"}</h2>
      <form id="platform-cost-form" class="form-grid">
        <div class="form-row">
          <label>${state.language === "it" ? "Data" : "Date"}<input name="cost_date" type="date" value="${new Date().toISOString().slice(0, 10)}" required></label>
          <label>${state.language === "it" ? "Categoria" : "Category"}<select name="category">
            <option value="supabase">Supabase</option>
            <option value="netlify">Netlify</option>
            <option value="openai">OpenAI</option>
            <option value="email">Email</option>
            <option value="domain">Domain</option>
            <option value="apple">Apple</option>
            <option value="other">Other</option>
          </select></label>
        </div>
        <label>Provider<input name="provider" placeholder="Supabase / Netlify / Resend / Namecheap" required></label>
        <label>${state.language === "it" ? "Descrizione" : "Description"}<input name="description"></label>
        <label>${state.language === "it" ? "Importo EUR" : "Amount EUR"}<input name="amount" type="number" min="0" step="0.01" required></label>
        <button class="secondary full" type="submit">${state.language === "it" ? "Registra costo" : "Save cost"}</button>
        <p class="form-message"></p>
      </form>
      <h3>${state.language === "it" ? "Cambio USD/EUR" : "USD/EUR exchange rate"}</h3>
      <form id="exchange-rate-form" class="form-grid">
        <label>1 USD = EUR<input name="rate" type="number" min="0.01" max="2" step="0.0001" value="${Number(summary.usd_to_eur || 0.92).toFixed(4)}" required></label>
        <button class="secondary full" type="submit">${state.language === "it" ? "Aggiorna conversione" : "Update conversion"}</button>
        <p class="form-message"></p>
      </form>
    </section>
    <aside class="card admin-cost-card admin-full-card" style="overflow:auto">
      <h2>${state.language === "it" ? "Costi registrati" : "Recorded costs"}</h2>
      <table class="admin-table"><thead><tr><th>Data</th><th>Cat.</th><th>Provider</th><th>Descrizione</th><th>EUR</th><th>Admin</th></tr></thead><tbody>${costRows}</tbody></table>
    </aside>
    <section class="card admin-cost-card admin-full-card" style="overflow:auto">
      <h2>${state.language === "it" ? "Token da revisionare" : "Token review"}</h2>
      <p style="color:var(--muted)">${state.language === "it" ? "Solo richieste AI sopra soglia costo/token, per controllo operativo." : "Only AI requests over cost/token thresholds, for operational review."}</p>
      <table class="admin-table"><thead><tr><th>Data</th><th>Utente</th><th>Esercizio</th><th>Token</th><th>USD</th><th>Motivo</th></tr></thead><tbody>${reviewRows}</tbody></table>
    </section>
    </div>
  </div>`;
}
